import React, { useState, useEffect } from 'react';
import { Row, Col, Button, Form, Spinner } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { LuMail, LuMessageCircle, LuSearch, LuPlus, LuMessageSquare, LuChevronDown } from "react-icons/lu";
import ConversationList from './ConversationList';
import ConversationThread from './ConversationThread';
import ComposeEmailModal from './ComposeEmailModal';
import WhatsAppInitiateModal from './WhatsAppInitiateModal';
import { getConversations, setFilter } from '../../redux/reducers/communicationSlice';

const CommunicationHub = () => {
  const dispatch = useDispatch();
  const { filters, currentConversation, loading } = useSelector((state) => state.communication);
  const businessId = useSelector((state) => state.business.selectedBusiness?._id);
  const [showCompose, setShowCompose] = useState(false);
  const [showWhatsApp, setShowWhatsApp] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showThreadOnMobile, setShowThreadOnMobile] = useState(false);

  useEffect(() => {
    if (businessId) {
      dispatch(getConversations({ ...filters, businessId }));
    }
    
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [dispatch, filters, businessId]);

  useEffect(() => {
    if (currentConversation && isMobile) {
      setShowThreadOnMobile(true);
    }
  }, [currentConversation, isMobile]);

  const handleFilterChange = (key, value) => {
    dispatch(setFilter({ key, value }));
  };

  return (
    <div className="communication-hub p-4">
      <Row className="g-4">
        {/* Left Panel: List */}
        <Col md={4} className={`${isMobile && showThreadOnMobile ? 'd-none' : 'd-block'}`}>
          <div className="card border-0 shadow-sm rounded-4 h-100 overflow-hidden" style={{ minHeight: 'calc(100vh - 150px)' }}>
            <div className="p-3 border-bottom">
              <div className="d-flex align-items-center gap-2 mb-3">
                <div className="rounded-2" style={{ width: 4, height: 18, background: "#28B0C4" }} />
                <h6 className="fw-bold text-dark mb-0">Messages</h6>
              </div>

              {/* Channel Pill Filters */}
              <div className="d-flex gap-2 mb-3 overflow-auto pb-1">
                {['all', 'email', 'whatsapp'].map((ch) => (
                  <Button
                    key={ch}
                    variant={filters.channel === ch ? 'primary' : 'light'}
                    size="sm"
                    className="rounded-pill px-3 fw-semibold text-capitalize"
                    style={{ 
                      fontSize: '12px',
                      background: filters.channel === ch ? '#28B0C4' : '#f1f5f9',
                      border: 'none',
                      color: filters.channel === ch ? 'white' : '#64748b'
                    }}
                    onClick={() => handleFilterChange('channel', ch)}
                  >
                    {ch === 'all' ? 'All' : ch === 'email' ? '📧 Email' : '💬 WhatsApp'}
                  </Button>
                ))}
              </div>

              {/* Search and Status */}
              <div className="d-flex gap-2">
                <div className="position-relative flex-grow-1">
                  <LuSearch className="position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" size={14} />
                  <Form.Control
                    type="text"
                    placeholder="Search..."
                    className="ps-5 py-2 border-0 bg-light rounded-3"
                    style={{ fontSize: '13px' }}
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                  />
                </div>
                <Form.Select
                  size="sm"
                  className="w-auto border-0 bg-light rounded-3 fw-semibold"
                  style={{ fontSize: '12px' }}
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                  <option value="archived">Archived</option>
                  <option value="all">All</option>
                </Form.Select>
              </div>
            </div>

            <ConversationList activeId={currentConversation?._id} />

            {/* Bottom Actions */}
            <div className="p-3 border-top d-flex gap-2 bg-white mt-auto">
              <Button 
                className="flex-grow-1 rounded-3 fw-semibold d-flex align-items-center justify-content-center gap-2"
                style={{ background: '#28B0C4', border: 'none', color: 'white', fontSize: '13px' }}
                onClick={() => setShowCompose(true)}
              >
                <LuPlus size={16} /> New Email
              </Button>
              <Button 
                className="rounded-3 fw-semibold d-flex align-items-center justify-content-center gap-2"
                style={{ background: '#d1fae5', color: '#10b981', border: 'none', fontSize: '13px' }}
                onClick={() => setShowWhatsApp(true)}
              >
                <LuMessageCircle size={16} /> WhatsApp
              </Button>
            </div>
          </div>
        </Col>

        {/* Right Panel: Thread */}
        <Col md={8} className={`${isMobile && !showThreadOnMobile ? 'd-none' : 'd-block'}`}>
          <div className="card border-0 shadow-sm rounded-4 h-100 overflow-hidden" style={{ minHeight: 'calc(100vh - 150px)' }}>
            <ConversationThread 
              isMobile={isMobile} 
              onBack={() => setShowThreadOnMobile(false)} 
            />
          </div>
        </Col>
      </Row>

      <ComposeEmailModal show={showCompose} onHide={() => setShowCompose(false)} />
      <WhatsAppInitiateModal show={showWhatsApp} onHide={() => setShowWhatsApp(false)} />
    </div>
  );
};

export default CommunicationHub;
